import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { listRefreshEvents } from '../services/listRefreshEvents';

/**
 * The reconciliation state of a single (person, device, credential_type) pair —
 * "is this credential actually confirmed present/absent on this device", as
 * opposed to DevicePersonAccessCache's synced_to_device flag, which is a
 * recomputed snapshot with no history of failed/unconfirmed attempts.
 */
export interface DeviceCredentialState {
  id: string;
  person_id: string;
  device_id: string;
  device_name: string;
  credential_type: string;
  state:
    | 'unknown'
    | 'grant_pending'
    | 'confirmed_present'
    | 'confirmed_absent'
    | 'grant_failed'
    | 'revoke_pending'
    | 'revoke_failed'
    | 'needs_reconciliation';
  last_attempt_at?: string;
  last_confirmed_at?: string;
  last_outcome_source?: string;
  last_error_message?: string;
  consecutive_failures: number;
  person_name?: string;
}

/** Fetch device credential reconciliation states for a single person (used in PermissionsTab). */
/** Window for folding a burst of per-device announcements into one refetch. */
const CREDENTIAL_REFRESH_COALESCE_MS = 150;

export const usePersonDeviceCredentialStates = (customerId?: string, personId?: string) => {
  const [states, setStates] = useState<DeviceCredentialState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sequence number of the newest request. Two refreshes can be in flight at once (a sync
  // finishing while the user reopens the tab), and the OLDER one may answer last — applying
  // it would park the panel on pre-sync data that looks authoritative.
  const latestRequest = useRef(0);

  const refetch = useCallback(async () => {
    if (!customerId || !personId) {
      setStates([]);
      return;
    }
    const seq = ++latestRequest.current;
    const isStale = () => seq !== latestRequest.current;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get<{ data: DeviceCredentialState[] }>(
        `/customers/${customerId}/people/${personId}/device-credential-states`
      );
      if (isStale()) return;
      setStates(response.data.data || []);
    } catch (err: any) {
      if (isStale()) return;
      console.error('Failed to fetch device credential states:', err);
      setError(err.response?.data?.error || 'Failed to load device credential states');
      setStates([]);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [customerId, personId]);

  useEffect(() => { refetch(); }, [refetch]);

  // Every consumer re-reads on the same signal, so two panels can never sit on different
  // snapshots of the same person — which is how the same readers once showed as
  // "Removido do dispositivo" and "Confirmado no dispositivo" side by side.
  //
  // Coalesced: syncing a person to twelve readers announces twelve times, and each
  // subscriber would otherwise fire twelve identical GETs for one button press.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = listRefreshEvents.subscribe('device-credentials', () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { refetch(); }, CREDENTIAL_REFRESH_COALESCE_MS);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [refetch]);

  return { states, loading, error, refetch };
};

/** Fetch the customer-wide reconciliation queue (needs_reconciliation / grant_failed / revoke_failed). */
export const useReconciliationQueue = (customerId?: string) => {
  const [items, setItems] = useState<DeviceCredentialState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!customerId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<{ data: DeviceCredentialState[] }>(
        `/customers/${customerId}/reconciliation/queue`
      );
      setItems(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to fetch reconciliation queue:', err);
      setError(err.response?.data?.error || 'Failed to load reconciliation queue');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { refetch(); }, [refetch]);

  const resolve = useCallback(async (stateId: string, resolution: 'confirmed_present' | 'confirmed_absent', notes?: string) => {
    if (!customerId) return;
    await api.post(`/customers/${customerId}/reconciliation/${stateId}/resolve`, { resolution, notes });
    await refetch();
  }, [customerId, refetch]);

  return { items, loading, error, refetch, resolve };
};
